import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import {
  MOBILE_PLATFORMS_NAMES_MAPPING,
  MOBILE_STORE_RANK_TOPS_NAMES_MAPPING,
  MINIGAME_STORE_RANK_TOPS_NAMES_MAPPING,
  PC_PLATFORMS,
  PC_PLATFORMS_NAMES_MAPPING,
  PC_STORE_RANK_TOPS_MAPPING,
  PC_STORE_RANK_TOPS_NAMES_MAPPING,
  CONSOLE_PLATFORMS,
  CONSOLE_PLATFORMS_NAMES_MAPPING,
  CONSOLE_PLATFORM_PLAYSTATION,
  WECHAT_PLATFORM_WECHAT,
  WECHAT_PLATFORM_DOUYIN,
  CONSOLE_SOURCES_PLAYSTATION_MONTHLY_RANK,
  WECHAT_SOURCES_PLAYSTATION_MONTHLY_RANK,
  WECHAT_SOURCES_PLAYSTATION_MONTHLY_RANK_DOUYIN,
  CONSOLE_STORE_RANK_TOPS_MAPPING,
  CONSOLE_STORE_RANK_TOPS_NAMES_MAPPING,
  RANK_TYPES,
  MOBILE_SORT_TOPS_KEYS,
  PC_SORT_TOPS_KEYS,
  CONSOLE_SORT_TOPS_KEYS,
  CONSOLE_SOURCE_PLAYSTATION_FREE_TO_PLAY,
  CONSOLE_SOURCE_PLAYSTATION_FREE_TO_PLAY_MONTHLY,
} from './config';
import { decimalFormat } from '@/pages/home/utils/common';
import type {
  IGameType,
  IDateTypely,
  ISelectOptionWithGroup,
  ITranslater,
} from '@/pages/intelligence/types/common';
import type {
  IMobileStoreRankPlatform,
  IPCStoreRankPlatform,
  IConsoleStoreRankPlatform,
  IMobileStoreRankTop,
  IPCStoreRankTop,
  IConsoleStoreRankTop,
  IStoreRankDetailsRankResData,
  IMobileStoreRankSource,
  IPCStoreRankSource,
  IConsoleStoreRankSource,
} from '@/pages/intelligence/types/storeRank';
import moment from 'moment';
import { STORERANK_METRICS_OPTIONS_LIST } from '@/pages/intelligence/_config/storeRank/config';

export const getValidTopChartName = (topChartsName: string, topChartsNames: string[]): string => {
  if (!Array.isArray(topChartsNames)) return topChartsName ?? '';
  return topChartsNames.indexOf(topChartsName) !== -1 ? topChartsName : topChartsNames[0];
};

const storeRankPlatformsNamesEntityMapping = {
  mobile: MOBILE_PLATFORMS_NAMES_MAPPING,
  pc: PC_PLATFORMS_NAMES_MAPPING,
  console: CONSOLE_PLATFORMS_NAMES_MAPPING,
};
export const getStoreRankPlatformName = (
  entityType: 'mobile' | 'pc' | 'console',
  platform: IPCStoreRankPlatform | IPCStoreRankPlatform | IConsoleStoreRankPlatform,
): string => {
  const storeRankPlatformsNamesMapping = storeRankPlatformsNamesEntityMapping[entityType];
  return storeRankPlatformsNamesMapping?.[platform] ?? platform ?? '';
};
export const getMobileStoreRankPlatformName = (platform: IMobileStoreRankPlatform): string =>
  getStoreRankPlatformName('mobile', platform);
export const getPCStoreRankPlatformName = (platform: IPCStoreRankPlatform): string =>
  getStoreRankPlatformName('pc', platform);
export const getConsoleStoreRankPlatformName = (platform: IConsoleStoreRankPlatform): string =>
  getStoreRankPlatformName('console', platform);

const storeRankTopsNamesEntityMapping: {
  mobile: {
    [key in IMobileStoreRankTop]: string;
  };
  pc: {
    [key in IPCStoreRankTop]: string;
  };
  console: {
    [key in IConsoleStoreRankTop]: string;
  };
  minigame: {
    [key in IMobileStoreRankTop]: string;
  };
} = {
  mobile: MOBILE_STORE_RANK_TOPS_NAMES_MAPPING,
  pc: PC_STORE_RANK_TOPS_NAMES_MAPPING,
  console: CONSOLE_STORE_RANK_TOPS_NAMES_MAPPING,
  minigame: MINIGAME_STORE_RANK_TOPS_NAMES_MAPPING,
};
export const getStoreRankTopName = (
  entityType: 'mobile' | 'pc' | 'console' | 'minigame',
  top: IMobileStoreRankTop | IPCStoreRankTop | IConsoleStoreRankTop,
  translater: ITranslater,
): string => {
  const storeRankTopsNamesMapping = storeRankTopsNamesEntityMapping[entityType];
  const translateKey = storeRankTopsNamesMapping?.[top];
  return translater({ id: translateKey }) as string;
};
export const getMobileStoreRankTopName = (
  top: IMobileStoreRankTop,
  translater: ITranslater,
): string => getStoreRankTopName('mobile', top, translater);
export const getPCStoreRankTopName = (top: IPCStoreRankTop, translater: ITranslater): string =>
  getStoreRankTopName('pc', top, translater);
export const getConsoleStoreRankTopName = (
  top: IConsoleStoreRankTop,
  translater: ITranslater,
): string => getStoreRankTopName('console', top, translater);

const mobileStoreRankTopSubTitles: {
  [key: string]: string;
} = {};
const minigameStoreRankTopSubTitles: {
  [key: string]: string;
} = {};
const pcStoreRankTopSubTitles: {
  [key: string]: string;
} = {
  'steam top sellers': 'dbi_store_rank_pc_top_steam_top_sellers_sub_title',
};
const consoleStoreRankTopSubTitles: {
  [key: string]: string;
} = {};
const storeRankTopSubTitlesMapping: {
  mobile: {
    [key: string]: string;
  };
  pc: {
    [key: string]: string;
  };
  console: {
    [key: string]: string;
  };
  minigame: {
    [key: string]: string;
  };
} = {
  mobile: mobileStoreRankTopSubTitles,
  pc: pcStoreRankTopSubTitles,
  console: consoleStoreRankTopSubTitles,
  minigame: minigameStoreRankTopSubTitles,
};
export const getStoreRankTopSubTitle = (
  entityType: 'mobile' | 'pc' | 'console' | 'minigame',
  source: string,
  translater: ITranslater,
): string => {
  const storeRankTopSubTitles = storeRankTopSubTitlesMapping[entityType];
  return (translater?.({ id: storeRankTopSubTitles?.[source] }) as string) || '';
};

export const getTopSource = (platform: string, top: string): string => {
  if (!platform || !top) return '';
  return `${platform?.toLowerCase()} ${top?.toLowerCase()}`;
};

export const getTopSources = (platform: string, top: string): string[] => {
  const source = getTopSource(platform, top);
  return source ? [source] : [];
};

/**
 * 解析榜单source，返回榜单所属平台、榜单，榜单souce是一个字符串
 * 1、榜单source 是 有平台、榜单组成，第一个单词 是 平台，后面的全部单词 是榜单
 * 2、PlayStation的月粒度榜单，接口返回的source 没有带平台前缀，需要特殊处理
 * @param source 榜单source
 * @returns 返回平台、榜单
 */
export const analysisPlatformTopInfo = (
  source: string,
): {
  platform: string;
  top: string;
} => {
  if (CONSOLE_SOURCES_PLAYSTATION_MONTHLY_RANK.includes(source)) {
    return { platform: CONSOLE_PLATFORM_PLAYSTATION, top: source };
  }
  if (WECHAT_SOURCES_PLAYSTATION_MONTHLY_RANK.includes(source)) {
    return { platform: WECHAT_PLATFORM_WECHAT, top: source };
  }
  if (WECHAT_SOURCES_PLAYSTATION_MONTHLY_RANK_DOUYIN.includes(source)) {
    return { platform: WECHAT_PLATFORM_DOUYIN, top: source };
  }
  const sourceInfos = source?.match(/^([^\s]+)\s+(.+)$/) ?? [];
  const platform = sourceInfos?.[1] ?? '';
  const top = sourceInfos?.[2] ?? '';
  return { platform, top };
};

export const analysisSourceMetricInfo = (
  sourceMetric: string,
  entityType?: string,
): {
  source: string;
  metric: string;
  metricKey: string;
} => {
  const sourceMetricInfos = sourceMetric?.split('|') ?? [];
  const source = sourceMetricInfos?.[0] ?? '';
  const metric = sourceMetricInfos?.[1] ?? '';
  let metricKey = '';
  if (entityType === 'pc') {
    metricKey = metric === 'rank' ? source : metric;
  } else {
    metricKey = source;
  }
  return { source, metric, metricKey };
};

export const getSourceMetric = (cfg?: { source?: string; metric?: string }): string => {
  const { source, metric } = cfg ?? {};
  if (!source || !metric) return '';
  return `${source}|${metric}`;
};

export const formatRankValue = (payload: any): number | string => {
  const num = Number.parseInt(payload, 10);
  // Rank 为0 也是无效数据
  if (Number.isNaN(num) || num === 0) return '-';
  return num;
};

export const formatRankRatioValue = (payload: any): number | string => {
  const num = Number(decimalFormat(payload, 1, false, ''));
  return num;
};

export const formatRankGrowthValue = (
  payload: any,
): {
  type: 'down' | 'up' | (string & {});
  value: string;
} => {
  const num = Number.parseInt(payload, 10);
  if (Number.isNaN(num))
    return {
      type: '',
      value: '-',
    };
  if (num === 0)
    return {
      type: '',
      value: '=',
    };
  return {
    type: num > 0 ? 'up' : 'down',
    value: String(Math.abs(num)),
  };
};

export const formatRankGrowthValueInt = (
  payload: any,
): {
  type: 'down' | 'up' | (string & {});
  value: string;
} => {
  const num = Number(decimalFormat(payload, 1, false, ''));
  if (Number.isNaN(num))
    return {
      type: '',
      value: '-',
    };
  if (num === 0)
    return {
      type: '',
      value: '=',
    };
  return {
    type: num > 0 ? 'up' : 'down',
    value: String(Math.abs(num)),
  };
};

// export const getIsMetricKey = (key: string, keys: string[]): boolean => {
//   if (!keys?.includes(key)) return false;
//   if (!key?.endsWith('_dod')) return true;
//   const ratioKey = `${key}_dod`;
//   return keys?.includes(ratioKey);
// };

export const sectionalRankMetricSorter = (
  a: IStoreRankDetailsRankResData,
  b: IStoreRankDetailsRankResData,
): number => {
  const aIndex = RANK_TYPES.indexOf(a?.name);
  const bIndex = RANK_TYPES.indexOf(b?.name);
  if (aIndex === -1) return 1;
  if (bIndex === -1) return 0;
  return aIndex - bIndex;
};

const pcConsolePlatformsMapping: {
  [key in 'pc' | 'console']: IPCStoreRankPlatform[] | IConsoleStoreRankPlatform[];
} = {
  pc: PC_PLATFORMS,
  console: CONSOLE_PLATFORMS,
};
export const getPCConsolePlatformsOptions = (
  entityType: 'pc' | 'console',
): ISelectOptionWithGroup[] => {
  const platforms = pcConsolePlatformsMapping[entityType];
  if (!Array.isArray(platforms)) return [];
  return platforms.map((platform) => {
    const platformIcon = getDataSourceIcon(platform);
    const platformName = getStoreRankPlatformName(entityType, platform);
    return {
      value: platform,
      label: platformName,
      text: platformName,
      icon: platformIcon,
    };
  });
};

export const STORERANK_DETAIL_PLATFORMS_OPTIONS_PC = getPCConsolePlatformsOptions('pc');
export const STORERANK_DETAIL_PLATFORMS_OPTIONS_CONSOLE = getPCConsolePlatformsOptions('console');

const pcConsoleTopsMapping: {
  pc: {
    [key in IPCStoreRankPlatform]: IPCStoreRankTop[];
  };
  console: {
    [key in IConsoleStoreRankPlatform]: IConsoleStoreRankTop[];
  };
} = {
  pc: PC_STORE_RANK_TOPS_MAPPING,
  console: CONSOLE_STORE_RANK_TOPS_MAPPING,
};
const pcConsoleTopsNamesMapping: {
  pc: {
    [key in IPCStoreRankTop]: string;
  };
  console: {
    [key in IConsoleStoreRankTop]: string;
  };
} = {
  pc: PC_STORE_RANK_TOPS_NAMES_MAPPING,
  console: CONSOLE_STORE_RANK_TOPS_NAMES_MAPPING,
};
export const getPCConsoleTopsOptionsMapping = (
  entityType: 'pc' | 'console',
): {
  [key: string]: ISelectOptionWithGroup[];
} => {
  const topsMapping = pcConsoleTopsMapping[entityType] ?? {};
  const topsNamesMapping = pcConsoleTopsNamesMapping[entityType] ?? {};
  return Object.entries(topsMapping).reduce((current, [key, value]) => {
    if (!key) return current;
    if (!Array.isArray(value)) return current;
    const valueTmp: ISelectOptionWithGroup[] = value.map((top) => {
      const topNameLocaleKey = topsNamesMapping[top];
      return {
        value: top,
        label: topNameLocaleKey,
        text: topNameLocaleKey,
        icon: '',
      };
    });
    return {
      ...current,
      [key]: valueTmp,
    };
  }, {});
};

const getChildOptionsMap = (platform: string) => {
  const metricObjList =
    STORERANK_METRICS_OPTIONS_LIST?.filter((item: any) => item.dataSource === platform) || [];
  return metricObjList?.map((metricItem: any) => {
    return {
      value: metricItem.value,
      label: metricItem.textKey,
      text: metricItem.textKey,
      icon: metricItem.dataSource,
    };
  });
};

// 根据数据库新字段value取值，集合新map
export const getPCConsoleTopsOptionsNewValueMapping = (
  entityType: 'pc' | 'console',
): {
  [key: string]: ISelectOptionWithGroup[];
} => {
  if (entityType === 'pc') {
    return {
      steam: getChildOptionsMap('steam'),
      epic: getChildOptionsMap('epic'),
    };
  }
  if (entityType === 'console') {
    return {
      playstation: getChildOptionsMap('playstation'),
      xbox: getChildOptionsMap('xbox'),
      nintendo: getChildOptionsMap('nintendo'),
    };
  }
  return {};
};

const SORTED_TOPS_KEYS_MAPPING: any = {
  mobile: MOBILE_SORT_TOPS_KEYS,
  pc: PC_SORT_TOPS_KEYS,
  console: CONSOLE_SORT_TOPS_KEYS,
};
export const getSortedTopsKeys = (
  entityType: IGameType,
  store: IMobileStoreRankPlatform | IPCStoreRankPlatform | IConsoleStoreRankPlatform,
  granularity: IDateTypely,
): IMobileStoreRankSource[] | IPCStoreRankSource[] | IConsoleStoreRankSource[] => {
  const sortedTopsKeys = SORTED_TOPS_KEYS_MAPPING[entityType];
  const key = `${granularity}_${store}`;
  return sortedTopsKeys?.[key];
};

export const handleStoreRankSource = (source: string): string => {
  if (source !== CONSOLE_SOURCE_PLAYSTATION_FREE_TO_PLAY_MONTHLY) return source;
  return CONSOLE_SOURCE_PLAYSTATION_FREE_TO_PLAY;
};

export const handleStoreRankSourceReq = (source: string, granularity: IDateTypely): string => {
  if (source !== CONSOLE_SOURCE_PLAYSTATION_FREE_TO_PLAY) return source;
  return granularity === 'monthly' ? CONSOLE_SOURCE_PLAYSTATION_FREE_TO_PLAY_MONTHLY : source;
};

export const handleStoreRankMetricRank = (metricRank: string): string => {
  if (metricRank !== 'ps free to play|rank') return metricRank;
  return 'playstation free to play|rank';
};

// 仅美国数据
export const checkNintendoUsOnly = (value: string) => {
  return ['nintendo new release', 'nintendo coming soon'].includes(value);
};

// 返回数据按时间排序
export const sortByDateCommonFun = (data: any[]) => {
  if (!Array.isArray(data)) return [];
  return data.sort(
    (a: any, b: any) =>
      new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
};

// 生成日期数组
const getDateArray = (startDate: string, endDate: string): string[] => {
  const dates: string[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return dates;
  }

  const current = new Date(start);
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

// 获取list以game_id相同的放到一个对象，并把所有同game_id的原list的item放到history里
export const getLineFormatData = (
  list: any[],
  initialTopChartsName: string,
  tableSelection: any[],
  startDate: string,
  endDate: string,
) => {
  const resIdList = tableSelection.map((item: any) => item.game_id);
  // 把list里面的game_id按resIdList排序
  const newList = list.sort((a, b) => {
    return resIdList.indexOf(a.game_id) - resIdList.indexOf(b.game_id);
  });
  if (!Array.isArray(newList) || newList.length === 0) {
    return {};
  }

  // 按 game_id 分组
  const gameMap: Record<string, any[]> = {};
  newList.forEach((item) => {
    const gameId = item?.game_id;
    if (!gameId) return;

    if (!gameMap[gameId]) {
      gameMap[gameId] = [];
    }
    gameMap[gameId].push(item);
  });

  // 转换为目标格式
  const gamesList = Object.entries(gameMap).map(([gameId, items]) => {
    const firstItem = items?.[0];
    // 以startDate为开始时间，endDate为结束时间，生成一个时间数组
    const dateArray = getDateArray(startDate, endDate);

    // 遍历dateArray，逐个匹配items，若缺失当前时间的items选项，则补上一个空对象
    // 补充的对象为items的第一个对象，重新定义game_rank属性为null
    const historyTemp: any = dateArray.map((date) => {
      const item = items.find((item) => item.date === date);

      if (item) {
        return item;
      }

      // 如果当前日期没有数据，使用items的第一个对象
      const baseItem = firstItem || {};

      return {
        ...baseItem,
        date,
        game_rank: null,
        avg_rank_30d: null,
      };
    });

    return {
      name: firstItem?.app_name || '',
      id: gameId,
      history: sortByDateCommonFun(historyTemp),
      cover: firstItem?.app_cover || '',
    };
  });
  // 如果指定了榜单名称，返回带榜单名称的对象格式
  if (initialTopChartsName) {
    return {
      [initialTopChartsName]: gamesList,
    } as any;
  }
  return gamesList;
};

// minigame接口调用参数统一转换方法
export const formatMiniGameParams = (params: any) => {
  // TODO: 实现minigame参数统一转换逻辑
  const orderMetric = params?.order_metric ?? 'game_rank';
  const order = params?.order ?? 'desc';

  // 验证 end_date 是否有效,并且是正确的日期格式
  const endDateStr = params?.end_date ?? '';

  // 验证日期格式：必须是 YYYY-MM-DD 格式
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  const isValidFormat = dateRegex.test(endDateStr);
  const endDateMoment = moment(endDateStr, 'YYYY-MM-DD', true);
  const isValidDate = endDateMoment.isValid();

  // 如果日期格式不正确或日期无效，使用当前日期
  const effectiveEndDate = (isValidFormat && isValidDate)
    ? endDateMoment
    : moment();
  return {
    // start_date: params?.start_date ?? '',
    // end_date: params?.end_date ?? '',
    start_date: effectiveEndDate.format('YYYY-MM-DD'),
    end_date: effectiveEndDate.format('YYYY-MM-DD'),
    minigame_types: params?.minigame_types || ['wechat_popularity'],
    markets: ['global'],
    granularity: 'daily',
    metrics: ['game_count'],
    order_by: [`${orderMetric} ${order}`],
    group_by: params?.group_by || ['game_id', 'minigame_type'],
    page: params?.page || 1,
    page_size: params?.page_size || 100,
    app_ids: params?.app_ids || [],
    filter: {
      minigame_genre: params?.minigame_genre || [],
    },
    top: params?.top || {},
    rank_keyword: params?.rank_keyword || '',
  };
};
